import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Eye, Pencil, DollarSign, Printer, Plus } from "lucide-react";

import "./ProjectsPage.css";

const PAGE_SIZE = 10;
const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL;

export default function ProjectsPage() {
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    status: "",
    search: "",
  });

  // Fetch projects from backend
  const fetchProjects = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE_URL}/projects`);
      setProjects(res.data || []);
    } catch (err) {
      console.error("Failed to fetch projects:", err.message);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  // Filter + Search logic
  const filteredProjects = useMemo(() => {
    if (!Array.isArray(projects)) return [];
    return projects.filter((project) => {
      const statusMatch =
        !filters.status || project.status.toLowerCase() === filters.status.toLowerCase();
      const searchMatch =
        !filters.search ||
        project.title.toLowerCase().includes(filters.search.toLowerCase()) ||
        project.description.toLowerCase().includes(filters.search.toLowerCase()) ||
        project.projectBudget.toString().includes(filters.search) ||
        project.amountRaised.toString().includes(filters.search);
      return statusMatch && searchMatch;
    });
  }, [projects, filters]);

  const totalPages = Math.ceil(filteredProjects.length / PAGE_SIZE) || 1;
  const paginatedProjects = filteredProjects.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  return (
    <div className="projects-page">
      {/* PAGE TITLE */}
      <h1 className="projects-page-title">Projects Management</h1>

      {/* ACTION BUTTONS */}
      <div className="projects-actions-row">
        <motion.button
          className="projects-btn-action"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate("/admin-panel/projects/post-project")}
        >
          <Plus size={20} style={{ marginRight: "8px" }} /> Post Project
        </motion.button>

        <motion.button
          className="projects-btn-action"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => window.print()}
        >
          <Printer size={20} style={{ marginRight: "8px" }} /> Print
        </motion.button>
      </div>

      {/* FILTER ROW */}
      <div className="projects-filters-row">
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        >
          <option value="">All Statuses</option>
          <option value="Completed">Completed</option>
          <option value="Ongoing">Ongoing</option>
          <option value="Upcoming">Upcoming</option>
        </select>

        <input
          type="text"
          placeholder="Search by Title, Description or Budget"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
        />
      </div>

      {/* TOTAL PROJECTS */}
      <div className="projects-total">
        Total Projects: {filteredProjects.length}
      </div>

      {/* PROJECTS TABLE */}
      <div className="projects-table-card">
        {loading ? (
          <div className="projects-loading">Loading projects…</div>
        ) : (
          <>
            <table className="projects-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Description</th>
                  <th>Budget</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedProjects.map((project) => (
                  <tr key={project._id}>
                    <td>{project.title}</td>
                    <td>{project.description}</td>
                    <td>
                      GHS {project.amountRaised.toLocaleString()} /{" "}
                      {project.projectBudget.toLocaleString()}
                    </td>
                    <td>{project.status}</td>
                    <td className="projects-actions">
                      <Eye
                        size={16}
                        onClick={() =>
                          navigate(`/admin-panel/projects/view/${project._id}`)
                        }
                      />
                      <Pencil
                        size={16}
                        onClick={() =>
                          navigate(`/admin-panel/projects/edit/${project._id}`)
                        }
                      />
                      <DollarSign
                        size={16}
                        onClick={() =>
                          navigate(`/admin-panel/projects/add-fund/${project._id}`)
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* PAGINATION */}
            <div className="projects-pagination">
              <button
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
